import { useEffect, useRef, useState } from "react";

// Only real Google Maps browser keys start with "AIza". Anything else (e.g. a
// Lovable connector token "lovc_...") would break the Maps JS loader, so it is ignored.
const isGoogleKey = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("AIza");

const ownKey = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY;
const connectorKey = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY;

// The linked connector owns the current custom-domain browser credential.
export const MAPS_BROWSER_KEY = (isGoogleKey(connectorKey)
  ? connectorKey
  : isGoogleKey(ownKey)
  ? ownKey
  : undefined) as string | undefined;
const BROWSER_KEY = MAPS_BROWSER_KEY;
const TRACKING_ID = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_TRACKING_ID as string | undefined;

declare global {
  interface Window {
    google?: any;
    __localMapInit?: () => void;
    gm_authFailure?: () => void;
  }
}

let loaderPromise: Promise<void> | null = null;

export function loadMapsApi(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.google?.maps?.Map) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  if (!BROWSER_KEY) return Promise.reject(new Error("no-key"));

  loaderPromise = new Promise<void>((resolve, reject) => {
    window.__localMapInit = () => resolve();
    const script = document.createElement("script");
    const channel = TRACKING_ID ? `&channel=${TRACKING_ID}` : "";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${BROWSER_KEY}&loading=async&callback=__localMapInit&language=es&region=ES${channel}`;
    script.async = true;
    script.onerror = () => reject(new Error("maps-load-error"));
    document.head.appendChild(script);
  });
  return loaderPromise;
}

interface LocalMapProps {
  latitude: number;
  longitude: number;
  title: string;
  zoom?: number;
  className?: string;
}

export function LocalMap({ latitude, longitude, title, zoom = 15, className }: LocalMapProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(!BROWSER_KEY);

  useEffect(() => {
    let cancelled = false;
    if (!BROWSER_KEY) return;

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (!cancelled) setFailed(true);
      previousAuthFailure?.();
    };

    loadMapsApi()
      .then(() => {
        if (cancelled || !ref.current) return;
        const position = { lat: latitude, lng: longitude };
        const map = new window.google.maps.Map(ref.current, {
          center: position,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          clickableIcons: false,
        });
        new window.google.maps.Marker({ map, position, title });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      window.gm_authFailure = previousAuthFailure;
    };
  }, [latitude, longitude, title, zoom]);

  if (failed) {
    return (
      <iframe
        title={`Mapa de ${title}`}
        width="100%"
        height="100%"
        style={{ border: 0, minHeight: 300 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={`https://www.google.com/maps?q=${latitude},${longitude}&z=${zoom}&output=embed`}
        allowFullScreen
        className={className}
      />
    );
  }

  return (
    <div
      ref={ref}
      role="application"
      aria-label={`Mapa de ${title}`}
      className={className}
      style={{ width: "100%", height: "100%", minHeight: 300 }}
    />
  );
}

export default LocalMap;
